// Ported from the design-time tool at ~/ui/GeneratePalette.vue, which is how
// fw.css's own default palettes were authored: a handful of hand-picked hex
// keyframes, interpolated across the 50-950 lightness scale with a monotonic
// cubic (PCHIP) spline so chroma/hue vary smoothly without overshoot.
//
// Everything past the initial hex parse stays in OKLCH -- fw.css's own
// palettes are hand-authored directly as oklch(), and the ramp is emitted
// the same way, so there's no round-trip through any other color space.

export type Lch = readonly [lightness: number, chroma: number, hue: number]

export interface RampStep {
  step: number
  lch: Lch
  oklch: string
}

const RAMP_STEPS = Array.from({ length: 19 }, (_, index) => (index + 1) * 50)

// Throws instead of returning undefined -- satisfies noUncheckedIndexedAccess
// for indices the surrounding logic already guarantees are valid.
export const at = <T>(array: ArrayLike<T>, index: number): T => {
  const value = array[index]
  if (value === undefined) throw new Error(`Index ${index} out of bounds`)
  return value
}

type Vec3 = readonly [number, number, number]
type Mat3 = readonly [Vec3, Vec3, Vec3]

const multiplyMatrix = (matrix: Mat3, vector: Vec3): Vec3 => {
  const dot = ([a, b, c]: Vec3): number => a * vector[0] + b * vector[1] + c * vector[2]
  const [row0, row1, row2] = matrix
  return [dot(row0), dot(row1), dot(row2)]
}

const RGB_TO_LMS: Mat3 = [
  [0.4122214708, 0.5363325363, 0.0514459929],
  [0.2119034982, 0.6806995451, 0.1073969566],
  [0.0883024619, 0.2817188376, 0.6299787005]
]
const LMS_TO_OKLAB: Mat3 = [
  [0.2104, 0.7936, -0.0040],
  [1.9779, -2.4285, 0.4506],
  [0.0259, 0.7827, -0.8086]
]

// The only place a color ever enters this module: a hex string from a native
// <input type="color">. Everything downstream (interpolation, output) works
// in OKLCH, so this hex -> OKLab -> OKLCH conversion happens once per picked
// color, never per ramp step.
export const parseColor = (hex: string): Lch => {
  const hexDigits = hex.trim().slice(1)
  const toChannel = (index: number) => Number.parseInt(hexDigits.substring(index, index + 2), 16) / 255
  const rgb: Vec3 = [toChannel(0), toChannel(2), toChannel(4)]

  const toLinear = (channel: number) => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  const linearRgb: Vec3 = [toLinear(rgb[0]), toLinear(rgb[1]), toLinear(rgb[2])]

  const lms = multiplyMatrix(RGB_TO_LMS, linearRgb)
  const cubeRootLms: Vec3 = [
    Math.cbrt(Math.max(lms[0], 1e-10)),
    Math.cbrt(Math.max(lms[1], 1e-10)),
    Math.cbrt(Math.max(lms[2], 1e-10))
  ]
  const [lightness, a, b] = multiplyMatrix(LMS_TO_OKLAB, cubeRootLms)

  const chroma = Math.sqrt(a * a + b * b)
  const hue = (Math.atan2(b, a) * 180 / Math.PI + 360) % 360

  return [lightness, chroma, hue]
}

const computeEdgeSlope = (h0: number, h1: number, d0: number, d1: number): number => {
  const slope = ((2 * h0 + h1) * d0 - h0 * d1) / (h0 + h1)
  if (Math.sign(slope) !== Math.sign(d0)) return 0
  if (Math.sign(d0) !== Math.sign(d1) && Math.abs(slope) > Math.abs(3 * d0)) return 3 * d0
  return slope
}

// PCHIP-style slopes (weighted harmonic mean interior + one-sided edges) so
// the spline stays monotonic between keyframes.
const computeSlopes = (xs: readonly number[], ys: readonly number[]): number[] => {
  const count = xs.length
  const slopes = Array.from<number>({ length: count }).fill(0)

  if (count < 2) return slopes
  if (count === 2) {
    slopes[0] = slopes[1] = (at(ys, 1) - at(ys, 0)) / (at(xs, 1) - at(xs, 0))
    return slopes
  }

  const stepWidths = new Float64Array(count - 1)
  const secantSlopes = new Float64Array(count - 1)
  for (let index = 0; index < count - 1; index++) {
    stepWidths[index] = at(xs, index + 1) - at(xs, index)
    secantSlopes[index] = (at(ys, index + 1) - at(ys, index)) / at(stepWidths, index)
  }

  for (let index = 1; index < count - 1; index++) {
    if (at(secantSlopes, index - 1) * at(secantSlopes, index) > 0) {
      const w1 = 2 * at(stepWidths, index) + at(stepWidths, index - 1)
      const w2 = at(stepWidths, index) + 2 * at(stepWidths, index - 1)
      slopes[index] = (w1 + w2) / (w1 / at(secantSlopes, index - 1) + w2 / at(secantSlopes, index))
    }
  }

  slopes[0] = computeEdgeSlope(at(stepWidths, 0), at(stepWidths, 1), at(secantSlopes, 0), at(secantSlopes, 1))
  slopes[count - 1] = computeEdgeSlope(
    at(stepWidths, count - 2), at(stepWidths, count - 3),
    at(secantSlopes, count - 2), at(secantSlopes, count - 3)
  )

  return slopes
}

class MonotonicCubicInterpolator {
  private readonly xs: readonly number[]
  private readonly ys: readonly number[]
  private readonly slopes: readonly number[]

  constructor(xs: readonly number[], ys: readonly number[]) {
    this.xs = xs
    this.ys = ys
    this.slopes = computeSlopes(xs, ys)
  }

  evaluate(x: number): number {
    const { xs, ys, slopes } = this
    const count = xs.length

    let index = 0
    if (x <= at(xs, 0)) {
      index = 0
    } else if (x >= at(xs, count - 1)) {
      index = count - 2
    } else {
      while (x > at(xs, index + 1)) index++
    }

    const x0 = at(xs, index)
    const x1 = at(xs, index + 1)
    const y0 = at(ys, index)
    const y1 = at(ys, index + 1)
    const m0 = at(slopes, index)
    const m1 = at(slopes, index + 1)

    const intervalWidth = x1 - x0
    const t = (x - x0) / intervalWidth

    const h00 = 2 * t ** 3 - 3 * t ** 2 + 1
    const h10 = t ** 3 - 2 * t ** 2 + t
    const h01 = -2 * t ** 3 + 3 * t ** 2
    const h11 = t ** 3 - t ** 2

    return y0 * h00 + intervalWidth * m0 * h10 + y1 * h01 + intervalWidth * m1 * h11
  }
}

// Hue is circular (350° and 10° are 20° apart through red, not 340° apart
// through green/cyan), so raw values are unwrapped into a continuous
// increasing/decreasing sequence before interpolating, then wrapped back
// into [0, 360) after evaluating.
const unwrapHues = (hues: readonly number[]): number[] => {
  const [first, ...rest] = hues
  if (first === undefined) return []

  const { unwrapped } = rest.reduce(
    ({ unwrapped, previous }, hue) => {
      const delta = (((hue - previous + 180) % 360 + 360) % 360) - 180
      const unwrappedHue = previous + delta
      return { unwrapped: [...unwrapped, unwrappedHue], previous: unwrappedHue }
    },
    { unwrapped: [first], previous: first }
  )
  return unwrapped
}

const wrapHue = (hue: number): number => ((hue % 360) + 360) % 360

interface Interpolator { evaluate: (x: number) => number }

const constantInterpolator = (value: number): Interpolator => ({ evaluate: () => value })

// A near-zero-chroma keyframe (white, near-black) has no real hue -- its a/b
// OKLab coordinates are ~0 by construction, so the computed hue is rounding
// noise, not signal. Fit hue only from keyframes with real chroma; fall back
// to all points only if none qualify (a fully achromatic ramp).
const CHROMA_EPSILON = 0.01

const buildHueInterpolator = (points: readonly Lch[]): Interpolator => {
  const saturated = points.filter(([, chroma]) => chroma > CHROMA_EPSILON)
  const relevant = saturated.length > 0 ? saturated : points

  const [firstHue, ...restHues] = unwrapHues(relevant.map(([, , h]) => h))
  if (firstHue === undefined) return constantInterpolator(0)
  if (restHues.length === 0) return constantInterpolator(firstHue)

  return new MonotonicCubicInterpolator(relevant.map(([l]) => l), [firstHue, ...restHues])
}

export interface PinnedStep {
  /** Ramp step to pin, e.g. 550 -- whichever step the consuming CSS actually renders with. */
  step: number
  lightness: number
  /**
   * When true, steps before the pin stay on the plain default curve,
   * untouched by the pin (used for background, so a dark/black pick can't
   * affect the light end). Omit to shape the whole ramp around the pin
   * instead, like primary/accent's button fill.
   */
  keepLightSideNatural?: boolean
}

/**
 * Turns a hand-picked set of light-to-dark OKLCH keyframes into a full
 * 50-950 ramp, the same way fw.css's own default palettes were built.
 *
 * @param points - Keyframes as [lightness, chroma, hue], any order -- sorted by lightness internally.
 * @param pin - When given, `pin.step` is pinned to exactly `pin.lightness` instead of the default curve -- see synthesizeRamp.
 */
export const generateRamp = (points: readonly Lch[], pin?: PinnedStep): RampStep[] => {
  const sorted = points.toSorted(([a], [b]) => a - b)

  const lightness = sorted.map(([l]) => l)
  const chromaValues = sorted.map(([, c]) => c)
  const interpolateChroma = new MonotonicCubicInterpolator(lightness, chromaValues)
  const interpolateHue = buildHueInterpolator(sorted)

  // The interior monotonic-cubic formula guarantees no overshoot, but the
  // one-sided edge-slope formula doesn't -- cap chroma to the most
  // saturated input keyframe as a safety net.
  const maxInputChroma = Math.max(...chromaValues)

  const topLightness = at(lightness, lightness.length - 1)
  const bottomLightness = at(lightness, 0)

  return RAMP_STEPS.map((step, index) => {
    const t = index / (RAMP_STEPS.length - 1)

    // Capped at 0.98: above ~1.0 is outside the representable sRGB range
    // and clips to white regardless of chroma.
    const natural = Math.min(0.98, 0.99 * (1 - t) ** 0.75 + 0.08)

    const targetLightness = ((): number => {
      if (pin === undefined) return natural
      const pinIndex = pin.step / 50 - 1

      if (pin.keepLightSideNatural) {
        return index < pinIndex
          ? natural
          : pin.lightness + (bottomLightness - pin.lightness) * ((index - pinIndex) / (RAMP_STEPS.length - 1 - pinIndex))
      }

      // Eased (exponent 1.2, not linear) so the light side doesn't drop
      // too fast toward a dark pin -- but only mildly: a stronger ease
      // flattens early steps together, losing contrast between e.g. the
      // sidebar and main content area.
      return index <= pinIndex
        ? topLightness + (pin.lightness - topLightness) * (index / pinIndex) ** 1.2
        : pin.lightness + (bottomLightness - pin.lightness) * ((index - pinIndex) / (RAMP_STEPS.length - 1 - pinIndex))
    })()

    const targetChroma = Math.min(maxInputChroma, Math.max(0, interpolateChroma.evaluate(targetLightness)))
    const targetHue = wrapHue(interpolateHue.evaluate(targetLightness))
    const lch: Lch = [targetLightness, targetChroma, targetHue]

    return {
      step,
      lch,
      oklch: `oklch(${targetLightness.toFixed(3)} ${targetChroma.toFixed(3)} ${targetHue.toFixed(2)})`
    }
  })
}

// A tinted (not pure white) light anchor keeps the low steps -- used for
// backgrounds -- from reading as plain white.
const LIGHT_ANCHOR_LIGHTNESS = 0.98
// Ceiling only; synthesizeRamp scales it down further for low-chroma picks
// so a near-gray input doesn't get an artificial, hue-noisy tint.
const MAX_LIGHT_ANCHOR_CHROMA = 0.03
// Keeps the light-anchor-to-pick interval wide enough that extrapolating
// past it doesn't overshoot for bright/saturated hues.
const MIN_LIGHT_ANCHOR_GAP = 0.15

// Adaptive like the light anchor: a fixed dark anchor would floor the
// darkest reachable step, so a black pick could never actually reach black.
const DARK_ANCHOR_LIGHTNESS = 0.08
const MIN_DARK_ANCHOR_GAP = 0.15

/**
 * v1 "simple" mode: a single picked color becomes the ramp's natural
 * keyframe, with a faint tint of the same hue near white and near black as
 * the other two stops.
 *
 * @param hex - The single picked color, e.g. "#3e809a".
 * @param pinStep - When given, that exact step reproduces the picked color
 * exactly -- for roles read at one specific step, like a button's fill.
 * Omit for roles read across a whole range of steps (e.g. background).
 * @param keepLightSideNatural - See PinnedStep.keepLightSideNatural.
 */
export const synthesizeRamp = (hex: string, pinStep?: number, keepLightSideNatural?: boolean): RampStep[] => {
  const picked = parseColor(hex)
  const [pickedLightness, pickedChroma, hue] = picked

  const lightAnchor: Lch = [
    Math.min(0.98, Math.max(LIGHT_ANCHOR_LIGHTNESS, pickedLightness + MIN_LIGHT_ANCHOR_GAP)),
    Math.min(MAX_LIGHT_ANCHOR_CHROMA, pickedChroma * 0.15),
    hue
  ]
  const darkAnchor: Lch = [
    Math.max(0, Math.min(DARK_ANCHOR_LIGHTNESS, pickedLightness - MIN_DARK_ANCHOR_GAP)),
    Math.min(MAX_LIGHT_ANCHOR_CHROMA, pickedChroma * 0.5),
    hue
  ]

  const pin = pinStep === undefined ? undefined : { step: pinStep, lightness: pickedLightness, keepLightSideNatural }
  return generateRamp([lightAnchor, picked, darkAnchor], pin)
}
